import type { ChartBarView } from "./chart-models.js";

const REGULAR_SESSION_SECONDS = 6.5 * 60 * 60;
const MARKET_OPEN_MINUTES = 9 * 60 + 30;
const SYNTHETIC_ORIGIN = Date.UTC(2020, 0, 6, 14, 30) / 1_000;

const easternClock = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  hour12: false,
  minute: "2-digit",
  second: "2-digit",
  timeZone: "America/New_York",
});

const easternAxisTime = new Intl.DateTimeFormat("en-US", {
  hour: "numeric",
  minute: "2-digit",
  timeZone: "America/New_York",
});

const easternFullTime = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  hour: "numeric",
  minute: "2-digit",
  month: "short",
  timeZone: "America/New_York",
  year: "numeric",
});

export interface MarketTimelineBar {
  readonly bar: ChartBarView;
  readonly chartTime: number;
}

export interface MarketTimelineLabel {
  readonly axis: string;
  readonly full: string;
}

export interface MarketTimelineSession {
  readonly closeTime: number;
  readonly openTime: number;
  readonly sessionDate: string;
}

export interface MarketTimeline {
  readonly bars: readonly MarketTimelineBar[];
  readonly labels: ReadonlyMap<number, MarketTimelineLabel>;
  readonly sessions: readonly MarketTimelineSession[];
}

function clockSeconds(value: string): number {
  const parts = Object.fromEntries(
    easternClock
      .formatToParts(new Date(value))
      .filter((part) => part.type !== "literal")
      .map((part) => [part.type, Number(part.value)]),
  );
  return (
    ((parts.hour ?? 0) * 60 + (parts.minute ?? 0) - MARKET_OPEN_MINUTES) * 60 + (parts.second ?? 0)
  );
}

function dateLabel(sessionDate: string): string {
  const [year, month, day] = sessionDate.split("-").map(Number);
  return new Intl.DateTimeFormat("en-US", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)));
}

function intervalSeconds(bars: readonly ChartBarView[]): number {
  const smallest = bars.reduce((minimum, bar) => {
    const duration = (Date.parse(bar.endedAt) - Date.parse(bar.startedAt)) / 1_000;
    return duration > 0 ? Math.min(minimum, duration) : minimum;
  }, Number.POSITIVE_INFINITY);
  return Math.max(60, Number.isFinite(smallest) ? smallest : 60);
}

export function buildMarketTimeline(
  bars: readonly ChartBarView[],
  timeframe: "intraday" | "daily" = "intraday",
): MarketTimeline {
  if (timeframe === "daily") {
    const labels = new Map<number, MarketTimelineLabel>();
    const dailyBars = [...bars]
      .sort((a, b) => a.sessionDate.localeCompare(b.sessionDate))
      .map((bar) => {
        // Session dates are calendar labels, not invented exchange open/close times.
        const chartTime = Date.parse(`${bar.sessionDate}T00:00:00Z`) / 1_000;
        labels.set(chartTime, { axis: dateLabel(bar.sessionDate), full: bar.sessionDate });
        return { bar, chartTime };
      });
    return { bars: dailyBars, labels, sessions: [] };
  }
  const regularBars = [...bars]
    .filter((bar) => bar.sessionPhase === "regular")
    .sort((left, right) => left.startedAt.localeCompare(right.startedAt));
  const gapSeconds = intervalSeconds(regularBars);
  const barsBySession = new Map<string, ChartBarView[]>();
  for (const bar of regularBars) {
    const session = barsBySession.get(bar.sessionDate) ?? [];
    session.push(bar);
    barsBySession.set(bar.sessionDate, session);
  }
  const labels = new Map<number, MarketTimelineLabel>();
  let nextOpenTime = SYNTHETIC_ORIGIN;
  const sessions = [...barsBySession].map(([sessionDate, sessionBars]) => {
    const finalBar = sessionBars.at(-1);
    const visibleSessionSeconds = Math.max(
      gapSeconds,
      Math.min(
        REGULAR_SESSION_SECONDS,
        finalBar === undefined ? REGULAR_SESSION_SECONDS : clockSeconds(finalBar.endedAt),
      ),
    );
    const openTime = nextOpenTime;
    const closeTime = openTime + visibleSessionSeconds;
    nextOpenTime = closeTime + gapSeconds;
    const date = dateLabel(sessionDate);
    labels.set(openTime, {
      axis: `${date} · 9:30 AM`,
      full: `${date}, ${sessionDate.slice(0, 4)}, 9:30 AM ET`,
    });
    const closeAxis =
      visibleSessionSeconds === REGULAR_SESSION_SECONDS
        ? "4:00 PM"
        : finalBar === undefined
          ? "Market close"
          : easternAxisTime.format(new Date(finalBar.endedAt));
    const closeFull =
      visibleSessionSeconds === REGULAR_SESSION_SECONDS
        ? `${date}, ${sessionDate.slice(0, 4)}, 4:00 PM ET`
        : finalBar === undefined
          ? `${date}, ${sessionDate.slice(0, 4)}, market close`
          : `${easternFullTime.format(new Date(finalBar.endedAt))} ET`;
    labels.set(closeTime, { axis: closeAxis, full: closeFull });
    return { closeTime, openTime, sessionDate };
  });
  const sessionByDate = new Map(sessions.map((session) => [session.sessionDate, session] as const));
  const timelineBars = regularBars.map((bar) => {
    const session = sessionByDate.get(bar.sessionDate);
    if (session === undefined) throw new Error("Regular market bar has no chart session.");
    const chartTime =
      session.openTime +
      Math.max(0, Math.min(session.closeTime - session.openTime - 1, clockSeconds(bar.startedAt)));
    labels.set(chartTime, {
      axis: easternAxisTime.format(new Date(bar.startedAt)),
      full: `${easternFullTime.format(new Date(bar.startedAt))} ET`,
    });
    return { bar, chartTime };
  });
  return { bars: timelineBars, labels, sessions };
}

export function chartTimeForInstant(timeline: MarketTimeline, value: string): number | undefined {
  if (timeline.bars.length === 0) return undefined;
  const target = Date.parse(value);
  let low = 0;
  let high = timeline.bars.length - 1;
  while (low < high) {
    const middle = Math.floor((low + high) / 2);
    const endedAt = Date.parse(timeline.bars[middle]?.bar.endedAt ?? "");
    if (endedAt < target) low = middle + 1;
    else high = middle;
  }
  const next = timeline.bars[low];
  const previous = timeline.bars[Math.max(0, low - 1)];
  if (next === undefined) return previous?.chartTime;
  if (previous === undefined) return next.chartTime;
  return Math.abs(Date.parse(previous.bar.endedAt) - target) <=
    Math.abs(Date.parse(next.bar.endedAt) - target)
    ? previous.chartTime
    : next.chartTime;
}
